import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import get from 'lodash.get';

import SelectableContainer from '../Tiles/SelectableContainer';
import Note from '../Tiles/Note';
import ItemPrice from './ItemPrice';
import ItemStatus from './ItemStatus';
import ItemShipping from './ItemShipping';
import ItemTitle from './ItemTitle';
import { ProjectTagTradeSourcingPromptModal } from '../Modals/ProjectTagTradeSourcingPromptModal';
import { TagProject } from '../Tiles/TagProject';

// Helpers
import actions from '../../actions/FavoritesActions';
import { deviceTypes, tabs } from '../../constants';
import {
    canShowProjectTagSourcingPromptModal,
    logProjectTagSourcingPromptModalView,
} from 'dibs-trade-sourcing/exports/sourcingPromptModalHelpers';
import classNames from 'classnames';

import styles from './styles/ListView.scss';

class ListRow extends React.Component {
    constructor(props) {
        super(props);

        this.selectItem = this.selectItem.bind(this);
        this.state = {
            isHovering: false,
            isTradeSourcingPromptModalOpen: false,
        };
    }

    selectItem(id, portItemId, imgSrc, selected, imgTitle) {
        if (!this.props.selecting) {
            this.props.setSelectMode(true);
        }
        if (selected) {
            const newSelectedItems = this.props.selectedItems.filter(
                item => item.portfolioItemId !== portItemId
            );
            if (this.props.selectedItems.length === 1) {
                this.props.setSelectMode(false);
            }
            this.props.selectItems(newSelectedItems);
            this.props.setSelectAll('');
        } else if (!selected) {
            if (this.props.totalItems === this.props.selectedItems.length + 1) {
                this.props.setSelectAll('select');
            }
            this.props.selectItems(
                this.props.selectedItems.concat([
                    {
                        itemId: id,
                        portfolioItemId: portItemId,
                        portfolioId: id,
                        src: imgSrc,
                        title: imgTitle,
                    },
                ])
            );
        }
    }

    render() {
        const {
            addNote,
            currency,
            deviceType,
            isPublic,
            item,
            noteContent,
            portfolioItemId,
            projects,
            selecting,
            showDealerBranding,
            showProjects,
            relay,
            reordering,
            selectedItems,
            tab,
            user,
        } = this.props;

        const onProjectTagSuccess = tagAdded => {
            if (tagAdded && canShowProjectTagSourcingPromptModal()) {
                this.setState({ isTradeSourcingPromptModalOpen: true });
            }
        };

        const tagProjectSource =
            tab === tabs.ITEMS ? 'favorites-items-list-view' : 'favorites-folder-list-view';

        const { firstPhotoWebPath, localizedPdpUrl, serviceId, title, seller } = item;
        const selected = selectedItems.find(
            selectedItem =>
                selectedItem.itemId === serviceId &&
                selectedItem.portfolioItemId === portfolioItemId
        );
        const rowClasses = classNames(styles.rowContainer, {
            [styles.rowSelected]: selected,
        });

        const colClasses = classNames(styles.col4, {
            [styles.col5]: showProjects,
            [styles.sellerCol]: showDealerBranding,
        });

        const selectableContainer = isPublic ? null : (
            <div className={styles.selectableContainer}>
                <SelectableContainer
                    isHovering={this.state.isHovering}
                    isTablet={deviceType === deviceTypes.TABLET}
                    reordering={reordering}
                    selected={selected}
                    selectItem={event => {
                        event.preventDefault();
                        this.selectItem(
                            serviceId,
                            portfolioItemId,
                            firstPhotoWebPath,
                            selected,
                            title
                        );
                    }}
                    selecting={selecting}
                    isListView
                />
            </div>
        );
        const sellerCol = !showDealerBranding ? null : (
            <div className={styles.colSellerName}>
                {selectableContainer}
                <div className={styles.titleContainer}>{get(seller, 'sellerProfile.company')}</div>
            </div>
        );

        return (
            <a
                className={rowClasses}
                onMouseEnter={() => this.setState({ isHovering: true })}
                onMouseLeave={() => this.setState({ isHovering: false })}
                href={localizedPdpUrl}
            >
                <div className={styles.row}>
                    {sellerCol}
                    <div className={styles.colTitle}>
                        {!showDealerBranding && selectableContainer}
                        <div
                            className={classNames({ [styles.titleContainer]: !showDealerBranding })}
                        >
                            <ItemTitle item={item} />
                        </div>
                    </div>
                    <div className={styles.colImage}>
                        <img
                            className={styles.thumbnail}
                            src={item.firstPhotoWebPath}
                            alt={item.title}
                        />
                    </div>
                    <div className={colClasses}>
                        <ItemPrice currency={currency} item={item} />
                    </div>
                    <div className={colClasses}>
                        <ItemShipping currency={currency} item={item} />
                    </div>
                    <div className={colClasses}>
                        <ItemStatus item={item} />
                    </div>
                    {showProjects && (
                        <div className={colClasses}>
                            <TagProject
                                projects={projects}
                                selecting={selecting}
                                isPublic={isPublic}
                                itemId={get(item, 'serviceId')}
                                userId={get(user, 'serviceId')}
                                source={tagProjectSource}
                                onSuccess={onProjectTagSuccess}
                                relayEnvironment={relay.environment}
                                isListView
                            />
                        </div>
                    )}
                    <div className={colClasses}>
                        <Note
                            disabled={selecting}
                            hasExistingNote={!!noteContent}
                            noteContent={noteContent}
                            onClick={event => {
                                event.preventDefault();
                                addNote({
                                    ...item,
                                    note: noteContent,
                                    portfolioItemId,
                                });
                            }}
                            isPublic={isPublic}
                            isListView
                        />
                    </div>
                </div>
                {this.state.isTradeSourcingPromptModalOpen && (
                    <ProjectTagTradeSourcingPromptModal
                        environment={relay.environment}
                        itemId={serviceId}
                        onClose={() => this.setState({ isTradeSourcingPromptModalOpen: false })}
                        onOpen={logProjectTagSourcingPromptModalView}
                    />
                )}
            </a>
        );
    }
}

ListRow.propTypes = {
    addNote: PropTypes.func,
    currency: PropTypes.string,
    deviceType: PropTypes.string,
    item: PropTypes.shape({
        classification: PropTypes.object,
        creators: PropTypes.array,
        firstPhotoWebPath: PropTypes.string,
        localizedPdpUrl: PropTypes.string,
        serviceId: PropTypes.string,
        seller: PropTypes.object,
        title: PropTypes.string,
    }),
    noteContent: PropTypes.string,
    projects: PropTypes.array,
    reordering: PropTypes.bool,
    relay: PropTypes.object,
    selecting: PropTypes.bool,
    isPublic: PropTypes.bool,
    portfolioItemId: PropTypes.string,
    shadowClasses: PropTypes.string,
    showDealerBranding: PropTypes.bool,
    setSelectMode: PropTypes.func,
    selectedItems: PropTypes.array,
    setSelectAll: PropTypes.func,
    selectItems: PropTypes.func,
    showProjects: PropTypes.bool,
    tab: PropTypes.string,
    totalItems: PropTypes.number,
    user: PropTypes.object,
    viewer: PropTypes.object,
};

const mapStateToProps = state => {
    return {
        deviceType: state.favorites.uiState.deviceType,
        reordering: state.favorites.uiState.reordering,
        selectAllStatus: state.favorites.uiState.selectAllStatus,
        selectedItems: state.favorites.uiState.selectedItems,
        selecting: state.favorites.uiState.selecting,
        tab: state.favorites.uiState.tab,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setSelectAll: selectAllStatus => {
            dispatch(actions.setSelectAll({ selectAllStatus }));
        },
        setSelectMode: selecting => {
            dispatch(actions.setSelectMode({ selecting }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(ListRow);
ListRow = createFragmentContainer(ReduxWrapped, {
    item: graphql`
        fragment ListRow_item on Item {
            localizedPdpUrl
            serviceId
            title
            isArt
            classification {
                creationDate
            }
            creators {
                creator {
                    displayName
                }
            }
            seller {
                serviceId
                sellerProfile {
                    company
                }
            }
            firstPhotoWebPath(size: large)
            ...ItemPrice_item
            ...ItemStatus_item
            ...ItemShipping_item
        }
    `,
});

export default ListRow;
